import {$qs, $qsAll} from '../../../@shared/ts/dom';

type ElementOptions = {
	loginId: string;
	clientKey: string;
	acceptSrc: string;
};

type Token = {
	token?: {
		descriptor: string;
		value: string;
	};
	error?: Array<{code: string; message: string}>;
	cancel?: boolean;
};

type AuthnetTokenizationResponse = {
	messages: {
		resultCode: 'Error' | 'Ok';
		message: Array<{
			code: string;
			text: string;
		}>;
	};
	opaqueData?: {
		dataDescriptor: string;
		dataValue: string;
	};
};

declare global {
	interface Window {
		peachpayAuthnetResponseHandler?: (response: AuthnetTokenizationResponse) => void;
	}
}

class AuthnetButtonElement {
	private static isTokenizing = false;

	private readonly loginId: string;
	private readonly clientKey: string;
	private readonly scriptSrc: string;

	private $button: HTMLButtonElement | null;
	private $script: HTMLScriptElement | null;

	constructor(options: ElementOptions) {
		this.loginId = options.loginId;
		this.clientKey = options.clientKey;

		this.scriptSrc = options.acceptSrc;

		this.$script = null;
		this.$button = null;
	}

	public async tokenize(type: 'bank' | 'card'): Promise<Token> {
		if (AuthnetButtonElement.isTokenizing) {
			throw new Error('Tokenization is already in progress. Only 1 tokenization attempt may occur at a time.');
		}

		AuthnetButtonElement.isTokenizing = true;

		await this.mount(type);

		// This is an artificial delay to let the just mounted popup script load a little and prevent some rendering artifacts.
		await new Promise<void>(res => {
			setTimeout(() => {
				res();
			}, 1000);
		});

		return new Promise<Token>((resolve, reject) => {
			if (!this.$button) {
				reject(new Error('Failed to locate Authorize.net tokenize button'));
			}

			window.peachpayAuthnetResponseHandler = (response: AuthnetTokenizationResponse) => {
				if (response.messages.resultCode === 'Ok' && response.opaqueData) {
					resolve({
						token: {
							descriptor: response.opaqueData.dataDescriptor,
							value: response.opaqueData.dataValue,
						},
					});
				} else if (response.messages.resultCode === 'Error') {
					resolve({
						error: response.messages.message.map(err => ({code: err.code, message: err.text})),
					});
				} else {
					reject(new Error('Unhandled Authorize.net tokenization response.'));
				}

				window.peachpayAuthnetResponseHandler = undefined;
				AuthnetButtonElement.isTokenizing = false;
			};

			this.$button?.click();

			const container = $qs('#AcceptUIContainer');
			const targetNode = $qs('#AcceptUIContainer')?.parentNode;
			const config = {attributes: false, childList: true, subtree: false};
			const observer = new MutationObserver(mutations => {
				for (const mutation of mutations) {
					if (mutation.type === 'childList' && mutation.removedNodes[0] === container) {
						observer.disconnect();
						resolve({
							cancel: true,
						});
						window.peachpayAuthnetResponseHandler = undefined;
						AuthnetButtonElement.isTokenizing = false;
					}
				}
			});

			if (targetNode) {
				observer.observe(targetNode, config);
			}
		});
	}

	private async mount(type: 'bank' | 'card' | 'all'): Promise<void> {
		// Make sure everything is cleaned up before opening!
		this.unmount();

		return new Promise((resolve, reject) => {
			const paymentOptions = {
				showCreditCard: type === 'card' || type === 'all',
				showBankAccount: type === 'bank' || type === 'all',
			};

			const $button = document.createElement('button');
			$button.classList.add('AcceptUI');
			$button.setAttribute('style', 'display: none;');
			$button.setAttribute('type', 'button');
			$button.setAttribute('data-billingAddressOptions', '{"show":false,"required":false}');
			$button.setAttribute('data-apiLoginID', this.loginId);
			$button.setAttribute('data-clientKey', this.clientKey);
			$button.setAttribute('data-acceptUIFormBtnTxt', 'Submit');
			$button.setAttribute('data-acceptUIFormHeaderTxt', 'Card Information');
			$button.setAttribute('data-paymentOptions', JSON.stringify(paymentOptions));
			$button.setAttribute('data-responseHandler', 'peachpayAuthnetResponseHandler');
			$button.textContent = 'Pay';
			this.$button = $button;

			const $script = document.createElement('script');
			$script.type = 'text/javascript';
			$script.src = this.scriptSrc;
			$script.classList.add('pp-authnet-accept-script');
			$script.onload = () => {
				resolve();
			};

			$script.onerror = () => {
				reject(new Error('Failed to load tokenization script.'));
			};

			this.$script = $script;

			document.body.appendChild($button);
			document.body.appendChild($script);
		});
	}

	private unmount() {
		this.$script?.remove();
		this.$button?.remove();

		$qsAll('#AcceptUIBackground,#AcceptUIContainer', $el => {
			$el.remove();
		});
	}
}

export {
	AuthnetButtonElement,
	type Token,
};
